import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { getUserFeedFieldPolicy } from './feedFieldPolicy';

function resolveGraphqlUrl(): string {
  if (import.meta.env.MODE !== 'production') {
    return 'http://localhost:4000/graphql';
  }

  if (import.meta.env.VITE_GRAPHQL_URL) {
    return import.meta.env.VITE_GRAPHQL_URL;
  }

  const base = import.meta.env.VITE_API_URL;
  if (!base) {
    throw new Error(
      'VITE_API_URL is not set. The production build cannot determine the GraphQL endpoint. ' +
        'Ensure VITE_API_URL or VITE_GRAPHQL_URL is defined in the environment when running vite build.',
    );
  }

  return `${base}/graphql`;
}

const API_URL = resolveGraphqlUrl();

const TOKEN_KEY = 'accessToken';

const httpLink = createHttpLink({
  uri: API_URL,
  credentials: 'include',
  fetchOptions: {
    mode: 'cors',
  },
});

const authMiddleware = new ApolloLink((operation, forward) => {
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem(TOKEN_KEY);
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    }));
  }

  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    for (const { message, locations, path } of graphQLErrors) {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      );
    }
  }
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

export const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          getDrafts: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
          getDraftById: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
          getUserFeed: getUserFeedFieldPolicy,
        },
      },
      PostDraft: {
        keyFields: ['id'],
        fields: {
          textBlocks: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
          textBlocksVersions: {
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  link: from([errorLink, authMiddleware, httpLink]),
  // `credentials` belongs on the HttpLink (set above), not the ApolloClient
  // constructor — @apollo/client 3.14 rejects it here (error 103). It was
  // redundant with the httpLink's `credentials: 'include'`.
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'ignore',
    },
    query: {
      // @ts-ignore
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
  },
});
